import { Film, KeyRound, Loader2, X } from 'lucide-react';
import { VideoProvider, VideoProviderStatus } from '../campaignsApi';
import './VideoGenerationModal.css';

interface VideoGenerationModalProps {
  open: boolean;
  provider: VideoProvider;
  statuses: VideoProviderStatus[];
  prompt: string;
  duration?: number;
  loadingPrompt: boolean;
  generating: boolean;
  isLight: boolean;
  onProviderChange: (provider: VideoProvider) => void;
  onPromptChange: (prompt: string) => void;
  onDurationChange?: (duration: number) => void;
  onConfigure: () => void;
  onCancel: () => void;
  onGenerate: () => void;
}

const providerLabels: Record<VideoProvider, string> = {
  fal: 'Fal.ai — Kling, Minimax e Wan',
  replicate: 'Replicate — Kling e Minimax',
  omnirouter: 'OmniRouter — Video Generations',
  kling: 'Kling direto — Video 3.0 Omni (4K)'
};

export function VideoGenerationModal(props: VideoGenerationModalProps) {
  if (!props.open) return null;

  const currentDuration = props.duration || 10;
  const estimatedCredits = currentDuration === 5 ? 18 : currentDuration === 15 ? 53 : currentDuration === 30 ? 105 : 35;
  const status = props.statuses.find((item) => item.provider === props.provider);
  const canGenerate = Boolean(status?.configured && props.prompt.trim() && !props.loadingPrompt);
  const themeClass = props.isLight ? ' video-generation-modal--light' : '';

  return (
    <div className={`video-generation-modal${themeClass}`} role="dialog" aria-modal="true" aria-labelledby="video-generation-title">
      <div className="video-generation-modal__card">
        <header className="video-generation-modal__header">
          <div className="video-generation-modal__title">
            <Film size={20} />
            <h3 id="video-generation-title">Gerar filme com IA</h3>
          </div>
          <button type="button" className="video-generation-modal__close" onClick={props.onCancel} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <label className="video-generation-modal__label" htmlFor="video-provider">Provider</label>
        <select
          id="video-provider"
          className="video-generation-modal__control"
          value={props.provider}
          onChange={(event) => props.onProviderChange(event.target.value as VideoProvider)}
        >
          {Object.entries(providerLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <div className={status?.configured ? 'video-generation-modal__status is-ready' : 'video-generation-modal__status is-missing'}>
          {status?.configured
            ? `Chave configurada: ${status.maskedKey}`
            : status?.invalidReason || 'Este provider ainda não possui chave válida para o tenant.'}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1rem 0 0.4rem' }}>
          <label className="video-generation-modal__label" htmlFor="video-duration" style={{ margin: 0 }}>
            Duração do Filme
          </label>
          <span style={{ 
            fontSize: '0.75rem', 
            fontWeight: 700, 
            color: props.isLight ? '#4f46e5' : '#818cf8', 
            background: props.isLight ? '#e0e7ff' : 'rgba(99, 102, 241, 0.15)', 
            padding: '2px 8px', 
            borderRadius: '12px' 
          }}>
            🪙 {estimatedCredits} créditos de IA
          </span>
        </div>
        <select
          id="video-duration"
          className="video-generation-modal__control"
          value={currentDuration}
          onChange={(event) => props.onDurationChange?.(Number(event.target.value))}
        >
          <option value={5}>⏱️ 5 segundos (Teaser Rápido) — 18 créditos</option>
          <option value={10}>⏱️ 10 segundos (Reels Padrão) — 35 créditos</option>
          <option value={15}>⏱️ 15 segundos (Stories Completo) — 53 créditos</option>
        </select>

        <label className="video-generation-modal__label" htmlFor="video-prompt">Revise e aprove o prompt</label>
        {props.loadingPrompt ? (
          <div className="video-generation-modal__loading"><Loader2 className="spin" size={18} /> Preparando prompt...</div>
        ) : (
          <textarea
            id="video-prompt"
            className="video-generation-modal__prompt"
            value={props.prompt}
            onChange={(event) => props.onPromptChange(event.target.value)}
            maxLength={2500}
            rows={10}
          />
        )}
        <div className="video-generation-modal__counter">{props.prompt.length}/2500 caracteres</div>

        <footer className="video-generation-modal__actions">
          {!status?.configured && (
            <button type="button" className="video-generation-modal__button is-key" onClick={props.onConfigure}>
              <KeyRound size={15} /> Configurar chave
            </button>
          )}
          <button type="button" className="video-generation-modal__button" onClick={props.onCancel}>Cancelar</button>
          <button
            type="button"
            className="video-generation-modal__button is-primary"
            disabled={!canGenerate || props.generating}
            onClick={props.onGenerate}
          >
            {props.generating ? <Loader2 className="spin" size={15} /> : <Film size={15} />}
            Validar e gerar
          </button>
        </footer>
      </div>
    </div>
  );
}
